<script setup>
import { useI18n } from 'vue-i18n'
import UserName from '@/components/UserName.vue'
import TrackName from '@/components/TrackName.vue'
import { formatDateTime } from '@/lib/dates'
import { deltaColor, formatDelta, formatRating } from '@/lib/rating'

/**
 * The latest races played: one card per race with the track, the moment it was raced and the
 * classification — position, best lap, race time and rating change of every driver.
 *
 * The cards are laid out in a grid — one column on small screens, two from the `md` breakpoint —
 * so a feed of twenty races is read as a grid.
 *
 * It only renders the races it is given (loading and errors stay with the caller, see
 * StandingsView), so the block can be moved — or shown — anywhere the same payload arrives.
 */
const props = defineProps({
  /** Races, in the shape returned by GET /api/races. */
  races: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const { t } = useI18n()

/**
 * Blocks sketched while loading: two are enough to show the shape of the feed — one row of the
 * grid — without pretending to know how many races are on their way.
 */
const SKELETON_RACES = 2
</script>

<template>
  <div>
    <div class="d-flex align-center ga-3 mb-3">
      <v-avatar color="primary" variant="tonal" size="48">
        <v-icon size="28">mdi-flag-checkered</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold mb-0">{{ t('races.title') }}</h2>
        <!-- <p class="text-caption text-medium-emphasis mb-0">{{ t('races.subtitle') }}</p> -->
      </div>
    </div>

    <!-- Loading: placeholders with the shape of a race card (header line plus classification). -->
    <div v-if="loading" class="races-grid">
      <v-card
        v-for="index in SKELETON_RACES"
        :key="index"
        elevation="3"
        rounded="lg"
        class="overflow-hidden"
      >
        <v-skeleton-loader
          :loading-text="t('races.loading')"
          type="subtitle, table-thead, table-row@4"
        />
      </v-card>
    </div>

    <v-card v-else-if="!races.length" elevation="3" rounded="lg" class="text-center pa-8">
      <v-icon size="56" color="medium-emphasis" class="mb-3">mdi-flag-outline</v-icon>
      <p class="text-medium-emphasis mb-0">{{ t('races.empty') }}</p>
    </v-card>

    <div v-else class="races-grid">
      <v-card v-for="race in races" :key="race.id" elevation="3" rounded="lg" class="overflow-hidden">
        <div class="pa-4">
          <div class="d-flex flex-wrap align-center ga-2 mb-3">
            <TrackName :track-id="race.trackId" class="text-subtitle-2 font-weight-bold" />
            <v-chip size="small" variant="tonal" prepend-icon="mdi-account-multiple" label>
              {{ t('races.driversCount', { count: race.players.length }) }}
            </v-chip>
            <v-chip size="small" variant="tonal" prepend-icon="mdi-counter" label>
              {{ t('races.laps') }}: {{ race.laps }}
            </v-chip>
            <v-chip size="small" variant="tonal" prepend-icon="mdi-clock-outline" label>
              {{ formatDateTime(race.dateEnd) }}
            </v-chip>
          </div>

          <v-table density="compact" hover>
            <thead>
              <tr>
                <th class="text-center" style="width: 56px">{{ t('standings.position') }}</th>
                <th>{{ t('races.player') }}</th>
                <th class="text-center text-no-wrap">{{ t('races.bestLap') }}</th>
                <th class="text-center text-no-wrap">{{ t('races.time') }}</th>
                <th class="text-center text-no-wrap">{{ t('races.rating') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="player in race.players"
                :key="player.userId"
                :class="{ 'me-row': player.isMe }"
              >
                <td class="text-center">{{ player.position }}</td>
                <td class="font-weight-medium"><UserName :name="player.playerName" /></td>
                <td class="text-center text-mono">{{ player.bestLapTime ?? '–' }}</td>
                <td class="text-center text-mono">
                  <span v-if="player.resultTime">{{ player.resultTime }}</span>
                  <!-- No race time: the driver started but never crossed the line. -->
                  <v-chip v-else size="x-small" variant="tonal" label>
                    {{ t('entryFlags.dnf') }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center ga-2">
                    <span class="font-weight-bold">{{ formatRating(player.ratingAfter) }}</span>
                    <v-chip
                      v-if="player.rankingDelta !== null"
                      :color="deltaColor(player.rankingDelta)"
                      size="x-small"
                      variant="flat"
                      label
                    >
                      {{ formatDelta(player.rankingDelta) }}
                    </v-chip>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<style scoped>
/* One race per column, two of them from the md breakpoint (960px) on: the feed reads as a grid
   instead of a single tall list. */
.races-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 960px) {
  .races-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* The logged-in player stands out without breaking the row rhythm of the table. */
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

/* Times are compared column by column: a fixed-width font (as in the leaderboard) keeps them
   aligned. */
.text-mono {
  font-family: monospace;
  font-size: 0.85rem;
}
</style>
